import React, { useCallback, useMemo } from 'react';

import { compose } from 'redux';
import { connect } from 'react-redux';
import { PropTypes } from 'prop-types';

import _ from 'lodash';
import {
  ErrorHandler, Loader, captureException, useCreateBillingPortalMutation,
} from '@playbookux/playbook-web-core';
import { useMutation, useQuery } from '@apollo/client';
import withLayout from '../../../core/components/with-layout';
import plans from '../../../core/constants/plans';
import ServiceSegment from './components/service-segment';

import {
  Container,
  Description,
  Title,
} from './styles';
import {
  CREATE_COMPANY_SERVICE_INTEGRATION,
  DELETE_COMPANY_SERVICE_INTEGRATION, GET_COMPANY_MEMBER_BY_USER_ID, GET_SERVICES,
} from './graphql';
import { findCurrentCompanyMember } from '../../../core/utils/find-current-company-member';

const Integrations = (props) => {
  const {
    companyId,
    me: currentUser,
  } = props;

  const {
    data: servicesData,
    loading: servicesLoading,
    error: servicesError,
  } = useQuery(GET_SERVICES);

  const {
    data: companyMembersData,
    loading: companyMemberLoading,
    error: companyMemberError,
    refetch: refetchCompany,
  } = useQuery(GET_COMPANY_MEMBER_BY_USER_ID, {
    variables: {
      userId: currentUser.user_id,
      companyId,
    },
  });

  const currentCompanyMember = useMemo(
    () => findCurrentCompanyMember(companyMembersData?.companyMembers, companyId),
    [companyMembersData, companyId],
  );

  const currentCompany = currentCompanyMember?.company;

  const hasRequiredPlan = useMemo(
    () => currentCompany?.plan === plans.SCALE,
    [currentCompany],
  );

  const [
    createCompanyServiceIntegration,
  ] = useMutation(CREATE_COMPANY_SERVICE_INTEGRATION, {
    refetchQueries: [GET_COMPANY_MEMBER_BY_USER_ID],
  });

  const [
    deleteCompanyServiceIntegration,
  ] = useMutation(DELETE_COMPANY_SERVICE_INTEGRATION, { awaitRefetchQueries: true });

  const handleDisconnect = useCallback(async (serviceIntegrationId) => {
    try {
      // invoke oauth flow
      const updatedCurrentCompany = await refetchCompany();
      const updatedCurrentCompanyMember = findCurrentCompanyMember(updatedCurrentCompany?.data?.companyMembers, companyId);
      const updatedCurrentCompanyMemberIntegrations = updatedCurrentCompanyMember?.companyServiceIntegrations;

      const updatedFilteredCompanyServiceIntegration = _.find(updatedCurrentCompanyMemberIntegrations,
        (companyServiceIntegration) => companyServiceIntegration.serviceIntegration.id === serviceIntegrationId);
      if (updatedFilteredCompanyServiceIntegration) {
        await deleteCompanyServiceIntegration({
          variables: {
            companyServiceIntegrationId: updatedFilteredCompanyServiceIntegration?.id,
          },
        });
      }
    } catch (error) {
      captureException('DELETE_COMPANY_SERVICE_INTEGRATION_FAILURE', error);
    }
  }, [companyId, deleteCompanyServiceIntegration, refetchCompany]);

  const handleConnect = useCallback(async (serviceIntegrationId) => {
    try {
      await createCompanyServiceIntegration({
        variables: {
          companyMemberId: currentCompanyMember?.id,
          serviceIntegrationId,
        },
      });
    } catch (error) {
      captureException('CREATE_COMPANY_SERVICE_INTEGRATION_FAILURE', error);
    }
  }, [createCompanyServiceIntegration, currentCompanyMember]);

  const {
    createPortal,
  } = useCreateBillingPortalMutation();

  const handleUpgrade = useCallback(() => {
    createPortal(currentCompany?.stripeCustomerId)
      .then((response) => {
        if (response) {
          window.open(response);
        }
      })
      .catch((error) => {
        captureException('CREATE_PORTAL_FAILURE', error);
      });
  }, [createPortal, currentCompany]);

  const services = useMemo(
    () => {
      if (servicesData?.serviceIntegrations) {
        return servicesData?.serviceIntegrations.map((service) => {
          const companyServiceIntegration = _.find(currentCompanyMember?.companyServiceIntegrations,
            (cServiceIntegration) => cServiceIntegration.serviceIntegration.id === service.id);
          return {
            ...service,
            companyServiceIntegrationId: companyServiceIntegration?.id,
            companyServiceIntegrationStatus: companyServiceIntegration?.status,
          };
        });
      }
      return [];
    },
    [currentCompanyMember, servicesData],
  );

  const renderServiceSegments = useCallback(() => {
    if (servicesLoading || companyMemberLoading) {
      return (
        <Loader data-loader="integrations-loader" />
      );
    } if (servicesError || companyMemberError) {
      return (
        <ErrorHandler
          error={servicesError || companyMemberError}
          defaultMessage="We couldn't load the integrations"
        />
      );
    }
    return (
      <>
        {/* One segment per service */
          services.map((service) => (
            <ServiceSegment
              key={service.id}
              service={service}
              onUpgrade={() => handleUpgrade()}
              onConnect={() => handleConnect(service.id)}
              onDisconnect={() => handleDisconnect(service.id)}
              companyMemberId={currentCompanyMember?.id}
              hasRequiredPlan={hasRequiredPlan}
              companyServiceIntegrationStatus={service.companyServiceIntegrationStatus}
            />
          ))
        }
      </>
    );
  }, [servicesLoading,
    companyMemberLoading,
    servicesError,
    companyMemberError,
    servicesData,
    currentCompanyMember,
    hasRequiredPlan,
    handleUpgrade, handleConnect, handleDisconnect]);

  return (
    <Container>
      <Title as="h2">Integrations</Title>
      <Description as="p">
        Connect to software that you already use.
        {' '}
        <a
          href="https://help.playbookux.com/en/articles/8022568-integrations"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more about integrations
        </a>
      </Description>
      {/* One segment per service */
        renderServiceSegments()
      }
    </Container>
  );
};

Integrations.propTypes = {
  companyId: PropTypes.string.isRequired,
  me: PropTypes.shape({
    email: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    user_id: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
  }).isRequired,
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
});

const enhance = compose(
  withLayout({ selectedTab: null }),
  connect(mapStateToProps, null),
);

export default enhance(Integrations);
